/**
 * SKRD guardrail frames and 180 × 54 toe boards (T-4161121).
 *
 * Both hook onto cups at each end, so an edge crossed by a stair entry or exit
 * gets neither — `railBlocked` samples the edge and the part is skipped, and it
 * is then not booked in the component list either.
 */

import { Group, Mesh } from "three";
import { CFG, GUARDRAIL_MID_HEIGHT, GUARDRAIL_TOP_HEIGHT, nodeY, TOE_LENGTHS } from "../../data/haki.ts";
import { dist, nearestMod, nearestOf } from "../../model/geometry.ts";
import { railBlocked } from "../../model/operations.ts";
import type { Point } from "../../model/types.ts";
import { G } from "../geometries.ts";
import { M } from "../materials.ts";
import { boxAt, tubeBetween, V, yawOf } from "../threeHelpers.ts";
import { DECK_LIFT, PLANK_THICKNESS, type RenderContext } from "./context.ts";

function floorTop(p: number): number {
  return nodeY(p) + DECK_LIFT + PLANK_THICKNESS;
}

/** Does a stair opening cross this edge anywhere along its length? */
function edgeBlocked(ctx: RenderContext, a: Point, b: Point, p: number): boolean {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const length = Math.hypot(dx, dz);
  const samples = Math.max(6, Math.round(length / 0.15));

  for (let i = 0; i <= samples; i++) {
    const point = { x: a.x + (dx * i) / samples, z: a.z + (dz * i) / samples };
    if (railBlocked(ctx.model, point, p, CFG.utv.width)) {
      return true;
    }
  }

  return false;
}

function attach(ctx: RenderContext, group: Group, type: "rail" | "toe", idx: number): void {
  for (const child of group.children) {
    child.userData = { type, idx };
    if (child instanceof Mesh) {
      ctx.pickElems.push(child);
    }
  }
  ctx.root.add(group);
}

export function renderRails(ctx: RenderContext): void {
  ctx.model.rails.forEach((rail, idx) => {
    if (edgeBlocked(ctx, rail.a, rail.b, rail.p)) {
      return;
    }

    const floor = floorTop(rail.p);
    const dx = rail.b.x - rail.a.x;
    const dz = rail.b.z - rail.a.z;
    const length = Math.hypot(dx, dz);
    const ex = dx / length;
    const ez = dz / length;
    const rot = yawOf(ex, ez);
    const inner = length - 0.12;
    const mx = (rail.a.x + rail.b.x) / 2;
    const mz = (rail.a.z + rail.b.z) / 2;

    const A = V(mx - (ex * inner) / 2, 0, mz - (ez * inner) / 2);
    const B = V(mx + (ex * inner) / 2, 0, mz + (ez * inner) / 2);
    const yTop = floor + GUARDRAIL_TOP_HEIGHT;
    const yMid = floor + GUARDRAIL_MID_HEIGHT;
    const group = new Group();

    group.add(tubeBetween(A.clone().setY(yTop), B.clone().setY(yTop), G.tube15, M.alu));
    group.add(tubeBetween(A.clone().setY(yMid), B.clone().setY(yMid), G.tube15, M.alu));
    for (const end of [A, B]) {
      group.add(tubeBetween(end.clone().setY(yMid - 0.05), end.clone().setY(yTop + 0.02), G.tube15, M.alu));
    }

    const posts = Math.max(1, Math.round(inner / 1));
    for (let i = 1; i < posts; i++) {
      const point = A.clone().lerp(B, i / posts);
      group.add(tubeBetween(point.clone().setY(yMid), point.clone().setY(yTop), G.tube27, M.alu));
    }

    for (const [px, pz, sign] of [
      [rail.a.x, rail.a.z, 1],
      [rail.b.x, rail.b.z, -1],
    ] as const) {
      for (const y of [yTop, yMid]) {
        group.add(boxAt(px + ex * sign * 0.055, y, pz + ez * sign * 0.055, 0.06, 0.05, 0.06, M.cast, rot));
      }
    }

    attach(ctx, group, "rail", idx);

    const mod = nearestMod(length * 1000);
    ctx.bom.add(
      "Guardrails",
      `Guardrail frame SKRD ${mod} AL`,
      CFG.skrd.art[mod] ?? "—",
      CFG.skrd.kg[mod] ?? 5,
    );
  });
}

export function renderToeBoards(ctx: RenderContext): void {
  ctx.model.toes.forEach((toe, idx) => {
    if (edgeBlocked(ctx, toe.a, toe.b, toe.p)) {
      return;
    }

    const floor = floorTop(toe.p);
    const dx = toe.b.x - toe.a.x;
    const dz = toe.b.z - toe.a.z;
    const length = Math.hypot(dx, dz);
    const ex = dx / length;
    const ez = dz / length;
    const rot = yawOf(ex, ez);
    const inner = length - 0.12;
    const cx = (toe.a.x + toe.b.x) / 2 + toe.inX * 0.03;
    const cz = (toe.a.z + toe.b.z) / 2 + toe.inZ * 0.03;

    const group = new Group();
    group.add(boxAt(cx, floor + 0.09, cz, 0.012, 0.18, inner, M.alu, rot));
    for (const sign of [1, -1]) {
      group.add(
        boxAt(
          cx + ex * sign * (inner / 2 - 0.02),
          floor + 0.14,
          cz + ez * sign * (inner / 2 - 0.02),
          0.054,
          0.09,
          0.03,
          M.cast,
          rot,
        ),
      );
    }

    attach(ctx, group, "toe", idx);

    const mod = nearestOf(TOE_LENGTHS, dist(toe.a, toe.b) * 1000);
    const art = CFG.toe.art[mod];
    const kg = CFG.toe.kg[mod];
    if (art !== undefined && kg !== undefined) {
      ctx.bom.add("Toe boards", `Toe board AL ${mod}`, art, kg);
    }
  });
}
